/**
 * API contracts for the `master-data` module (M1) — zod schemas + inferred types
 * shared by apps/api, apps/web and (via generated types) apps/android.
 *
 * OWNERSHIP: the master-data module agent owns this file exclusively.
 * Import from '@warehouse/shared'.
 *
 * Covered surface: articles (+ per-article unit conversions), article
 * categories, units, article barcodes (GTIN + supplier-scoped raw, teach flow,
 * resolution), suppliers, restaurants and the `restaurant_users` mapping.
 *
 * Two rules are encoded here rather than left to the UI:
 *
 * 1. **BC-synced fields are read-only.** A BC-owned column on a synced row is
 *    absent from the update schema's writable set; the API answers `E_CONFLICT`
 *    with `{ reason: 'bc_synced', fields }` instead of quietly accepting an
 *    edit BC will overwrite tonight (the web renders "Synkad från BC").
 * 2. **A barcode is never stolen.** Binding a code that another article already
 *    owns in the same scope fails with `E_CONFLICT` +
 *    `barcodeConflictParamsSchema` — the old WMS's `onConflictDoUpdate` silently
 *    re-pointed an EAN warehouse-wide (docs/recon/bc-inventory-stocktaking.md
 *    §2 "the ownership-transfer hazard"). Moving a barcode is a separate,
 *    deliberate, audited edit.
 */
import { z } from 'zod';

import { qtySchema, uuidSchema } from '../schemas/common.js';

// ---------------------------------------------------------------------------
// primitives
// ---------------------------------------------------------------------------

const shortText = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullish();

export const temperatureClassSchema = z.enum(['dry', 'chilled', 'frozen', 'produce']);
export type TemperatureClass = z.infer<typeof temperatureClassSchema>;

export const barcodeKindSchema = z.enum(['gtin', 'raw']);
export type BarcodeKind = z.infer<typeof barcodeKindSchema>;

/** How a scan was matched to an article. `none` = nothing matched. */
export const barcodeMatchSourceSchema = z.enum(['gtin', 'raw', 'article-no', 'none']);
export type BarcodeMatchSource = z.infer<typeof barcodeMatchSourceSchema>;

/**
 * BC linkage, shown by the web as "Synkad från BC". `syncedFields` is what the
 * form must render read-only; the API rejects writes to them.
 */
export const bcLinkSchema = z.object({
  synced: z.boolean(),
  bcNo: z.string().nullable(),
  syncedAt: z.iso.datetime().nullable(),
  syncedFields: z.array(z.string()),
});
export type BcLink = z.infer<typeof bcLinkSchema>;

/** `E_CONFLICT` params when an edit targets a BC-owned column. */
export const bcReadOnlyParamsSchema = z.object({
  reason: z.literal('bc_synced'),
  entity: z.string(),
  fields: z.array(z.string()),
});
export type BcReadOnlyParams = z.infer<typeof bcReadOnlyParamsSchema>;

// ---------------------------------------------------------------------------
// units
// ---------------------------------------------------------------------------

export const unitSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Unit = z.infer<typeof unitSchema>;

export const unitCreateSchema = z.object({
  /** `ST`, `KG`, `KRT`, `PALL`… stored upper-cased. */
  code: shortText(20).transform((v) => v.toUpperCase()),
  name: shortText(80),
});
export type UnitCreate = z.infer<typeof unitCreateSchema>;

export const unitUpdateSchema = unitCreateSchema.partial();
export type UnitUpdate = z.infer<typeof unitUpdateSchema>;

// ---------------------------------------------------------------------------
// article categories
// ---------------------------------------------------------------------------

export const articleCategorySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** BC `itemCategoryCode` join key: `TORRT` / `KYLT` / `FRYST` / … */
  bcName: z.string().nullable(),
  temperatureClass: temperatureClassSchema.nullable(),
  sort: z.number().int(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ArticleCategory = z.infer<typeof articleCategorySchema>;

export const articleCategoryCreateSchema = z.object({
  name: shortText(80),
  bcName: optionalText(80),
  temperatureClass: temperatureClassSchema.nullish(),
  sort: z.coerce.number().int().min(0).max(9999).default(0),
});
export type ArticleCategoryCreate = z.infer<typeof articleCategoryCreateSchema>;

export const articleCategoryUpdateSchema = articleCategoryCreateSchema.partial();
export type ArticleCategoryUpdate = z.infer<typeof articleCategoryUpdateSchema>;

// ---------------------------------------------------------------------------
// suppliers
// ---------------------------------------------------------------------------

export const supplierSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** short code used to build produce auto-lots `<supplier-code>-<yyyymmdd>` */
  code: z.string().nullable(),
  bcVendorNo: z.string().nullable(),
  orgNo: z.string().nullable(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  countryCode: z.string().nullable(),
  isActive: z.boolean(),
  desadvEnabled: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Supplier = z.infer<typeof supplierSchema>;

export const supplierDetailSchema = supplierSchema.extend({
  bc: bcLinkSchema,
  articleCount: z.number().int().nonnegative(),
  barcodeCount: z.number().int().nonnegative(),
});
export type SupplierDetail = z.infer<typeof supplierDetailSchema>;

/**
 * `bcVendorNo` is deliberately NOT writable: only the bc-integration module
 * (M8) links a supplier to a BC vendor, so a typo here can never orphan a sync.
 */
export const supplierCreateSchema = z.object({
  name: shortText(160),
  code: optionalText(20).transform((v) => (typeof v === 'string' ? v.toUpperCase() : v)),
  orgNo: optionalText(40),
  contactName: optionalText(120),
  contactEmail: optionalText(160),
  contactPhone: optionalText(60),
  addressLine1: optionalText(160),
  addressLine2: optionalText(160),
  postalCode: optionalText(20),
  city: optionalText(80),
  countryCode: optionalText(2).transform((v) => (typeof v === 'string' ? v.toUpperCase() : v)),
  isActive: z.boolean().default(true),
  desadvEnabled: z.boolean().default(false),
});
export type SupplierCreate = z.infer<typeof supplierCreateSchema>;

export const supplierUpdateSchema = supplierCreateSchema.partial();
export type SupplierUpdate = z.infer<typeof supplierUpdateSchema>;

/** BC owns the vendor's display name once the supplier is linked. */
export const SUPPLIER_BC_OWNED_FIELDS = ['name'] as const;

// ---------------------------------------------------------------------------
// restaurants (BC customers; the old WMS called them "locations")
// ---------------------------------------------------------------------------

export const restaurantSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  code: z.string(),
  bcCustomerNo: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  countryCode: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Restaurant = z.infer<typeof restaurantSchema>;

export const restaurantDetailSchema = restaurantSchema.extend({
  bc: bcLinkSchema,
  userCount: z.number().int().nonnegative(),
});
export type RestaurantDetail = z.infer<typeof restaurantDetailSchema>;

export const restaurantCreateSchema = z.object({
  name: shortText(160),
  code: shortText(20).transform((v) => v.toUpperCase()),
  addressLine1: optionalText(160),
  addressLine2: optionalText(160),
  postalCode: optionalText(20),
  city: optionalText(80),
  countryCode: optionalText(2).transform((v) => (typeof v === 'string' ? v.toUpperCase() : v)),
  contactEmail: optionalText(160),
  contactPhone: optionalText(60),
  isActive: z.boolean().default(true),
});
export type RestaurantCreate = z.infer<typeof restaurantCreateSchema>;

export const restaurantUpdateSchema = restaurantCreateSchema.partial();
export type RestaurantUpdate = z.infer<typeof restaurantUpdateSchema>;

export const RESTAURANT_BC_OWNED_FIELDS = ['name'] as const;

/** A `restaurant_users` row, joined with the user it maps. */
export const restaurantUserSchema = z.object({
  userId: uuidSchema,
  restaurantId: uuidSchema,
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  roleKeys: z.array(z.string()),
  isActive: z.boolean(),
  restaurantName: z.string(),
  restaurantCode: z.string(),
  createdAt: z.iso.datetime(),
});
export type RestaurantUser = z.infer<typeof restaurantUserSchema>;

export const restaurantUserLinkSchema = z.object({
  userId: uuidSchema,
  restaurantId: uuidSchema,
});
export type RestaurantUserLink = z.infer<typeof restaurantUserLinkSchema>;

/** Candidate rows for the "map a user" picker. */
export const restaurantUserCandidateSchema = z.object({
  id: uuidSchema,
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  roleKeys: z.array(z.string()),
  isActive: z.boolean(),
  restaurantIds: z.array(uuidSchema),
});
export type RestaurantUserCandidate = z.infer<typeof restaurantUserCandidateSchema>;

// ---------------------------------------------------------------------------
// articles
// ---------------------------------------------------------------------------

export const articleSchema = z.object({
  id: uuidSchema,
  articleNo: z.string(),
  name: z.string(),
  nameZh: z.string().nullable(),
  categoryId: uuidSchema.nullable(),
  baseUnitId: uuidSchema,
  supplierId: uuidSchema.nullable(),
  /** spec §5.3: controls whether the article appears in the restaurant portal */
  orderable: z.boolean(),
  priority: z.number().int(),
  lotTracked: z.boolean(),
  expiryRequired: z.boolean(),
  catchWeight: z.boolean(),
  minStockBase: qtySchema.nullable(),
  maxStockBase: qtySchema.nullable(),
  reorderPointBase: qtySchema.nullable(),
  leadTimeDays: z.number().int().nullable(),
  shelfLifeDays: z.number().int().nullable(),
  isActive: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Article = z.infer<typeof articleSchema>;

/** One row of the article register list — joined for display, never re-queried per row. */
export const articleListItemSchema = articleSchema.extend({
  categoryName: z.string().nullable(),
  baseUnitCode: z.string(),
  supplierName: z.string().nullable(),
  barcodeCount: z.number().int().nonnegative(),
  bcSynced: z.boolean(),
});
export type ArticleListItem = z.infer<typeof articleListItemSchema>;

/** A per-article UoM conversion (`article_units`). */
export const articleUnitSchema = z.object({
  id: uuidSchema,
  articleId: uuidSchema,
  unitId: uuidSchema,
  unitCode: z.string(),
  unitName: z.string(),
  /** how many BASE units one of this unit contains */
  qtyInBase: qtySchema,
  isDefaultSales: z.boolean(),
  isDefaultPurchase: z.boolean(),
  isBaseUnit: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ArticleUnit = z.infer<typeof articleUnitSchema>;

export const articleBarcodeSchema = z.object({
  id: uuidSchema,
  articleId: uuidSchema,
  barcode: z.string(),
  kind: barcodeKindSchema,
  supplierId: uuidSchema.nullable(),
  supplierName: z.string().nullable(),
  unitId: uuidSchema.nullable(),
  unitCode: z.string().nullable(),
  /** base units added per scan of this code */
  qtyPerScan: qtySchema,
  taughtBy: uuidSchema.nullable(),
  taughtByName: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ArticleBarcode = z.infer<typeof articleBarcodeSchema>;

/** The barcode-admin list row: same, plus the article it points at. */
export const barcodeListItemSchema = articleBarcodeSchema.extend({
  articleNo: z.string(),
  articleName: z.string(),
});
export type BarcodeListItem = z.infer<typeof barcodeListItemSchema>;

export const articleDetailSchema = articleListItemSchema.extend({
  bc: bcLinkSchema,
  units: z.array(articleUnitSchema),
  barcodes: z.array(articleBarcodeSchema),
});
export type ArticleDetail = z.infer<typeof articleDetailSchema>;

export const articleCreateSchema = z.object({
  articleNo: shortText(60).transform((v) => v.toUpperCase()),
  name: shortText(200),
  nameZh: optionalText(200),
  categoryId: uuidSchema.nullish(),
  baseUnitId: uuidSchema,
  supplierId: uuidSchema.nullish(),
  orderable: z.boolean().default(true),
  priority: z.coerce.number().int().min(-1000).max(1000).default(0),
  lotTracked: z.boolean().default(true),
  expiryRequired: z.boolean().default(false),
  catchWeight: z.boolean().default(false),
  minStockBase: qtySchema.nullish(),
  maxStockBase: qtySchema.nullish(),
  reorderPointBase: qtySchema.nullish(),
  leadTimeDays: z.coerce.number().int().min(0).max(3650).nullish(),
  shelfLifeDays: z.coerce.number().int().min(0).max(36500).nullish(),
  isActive: z.boolean().default(true),
});
export type ArticleCreate = z.infer<typeof articleCreateSchema>;

export const articleUpdateSchema = articleCreateSchema.partial();
export type ArticleUpdate = z.infer<typeof articleUpdateSchema>;

/**
 * Columns the BC item sync owns (`number` → article_no, `displayName` → name,
 * `baseUnitOfMeasureCode` → base unit, `itemCategoryCode` → category). Every
 * other column — orderable, priority, lot/expiry flags, min/max/reorder — is
 * WMS-owned and stays editable on a synced article.
 */
export const ARTICLE_BC_OWNED_FIELDS = ['articleNo', 'name', 'baseUnitId', 'categoryId'] as const;

export const articleUnitUpsertSchema = z.object({
  unitId: uuidSchema,
  qtyInBase: qtySchema,
  isDefaultSales: z.boolean().default(false),
  isDefaultPurchase: z.boolean().default(false),
});
export type ArticleUnitUpsert = z.infer<typeof articleUnitUpsertSchema>;

// ---------------------------------------------------------------------------
// barcodes
// ---------------------------------------------------------------------------

/**
 * Manual barcode↔article(+supplier) registration (Victor Q16). `kind` may be
 * omitted: the server classifies the code (a valid GTIN-8/12/13/14 becomes a
 * `gtin` normalised to GTIN-14, anything else a supplier-scoped `raw` code).
 */
export const barcodeCreateSchema = z.object({
  articleId: uuidSchema,
  barcode: shortText(64),
  kind: barcodeKindSchema.optional(),
  /** required for `raw` codes — they are only unique within a supplier */
  supplierId: uuidSchema.nullish(),
  unitId: uuidSchema.nullish(),
  qtyPerScan: qtySchema.default('1'),
  note: optionalText(300),
});
export type BarcodeCreate = z.infer<typeof barcodeCreateSchema>;

export const barcodeUpdateSchema = z.object({
  /** moving a barcode to another article is an explicit, audited edit */
  articleId: uuidSchema.optional(),
  supplierId: uuidSchema.nullish(),
  unitId: uuidSchema.nullish(),
  qtyPerScan: qtySchema.optional(),
  note: optionalText(300),
});
export type BarcodeUpdate = z.infer<typeof barcodeUpdateSchema>;

/**
 * Teach-on-first-scan (permission `warehouse.barcodes.teach`). Same write as
 * `barcodeCreateSchema` plus the raw scan for the audit trail — the operator
 * scanned a physical label and we record exactly what came off it.
 */
export const barcodeTeachSchema = barcodeCreateSchema.extend({
  /** the untouched scanner payload, symbology identifier included */
  raw: z.string().min(1).max(512),
});
export type BarcodeTeach = z.infer<typeof barcodeTeachSchema>;

/**
 * `E_CONFLICT` params when a barcode is already bound. The web renders
 * "Streckkoden används redan av …" with a link — it never offers a silent
 * takeover.
 */
export const barcodeConflictParamsSchema = z.object({
  reason: z.literal('barcode_taken'),
  barcode: z.string(),
  articleId: uuidSchema,
  articleNo: z.string(),
  articleName: z.string(),
  supplierId: uuidSchema.nullable(),
});
export type BarcodeConflictParams = z.infer<typeof barcodeConflictParamsSchema>;

// ---------------------------------------------------------------------------
// barcode resolution — the port receiving (M3) and the scanner app consume
// ---------------------------------------------------------------------------

export const barcodeResolveRequestSchema = z.object({
  /** whatever the scanner/camera produced, unmodified */
  raw: z.string().min(1).max(512),
  /** narrows supplier-scoped raw codes; usually the PO's supplier */
  supplierId: uuidSchema.nullish(),
});
export type BarcodeResolveRequest = z.infer<typeof barcodeResolveRequestSchema>;

/** What `@warehouse/gs1` made of the scan, projected to what the WMS consumes. */
export const barcodeScanSummarySchema = z.object({
  kind: z.enum(['gs1', 'ean', 'raw']),
  gtin14: z.string().nullable(),
  sscc: z.string().nullable(),
  serial: z.string().nullable(),
  lot: z.string().nullable(),
  bestBefore: z.string().nullable(),
  /** AI 37 / 30 — trade items in the logistic unit */
  count: z.number().nullable(),
  /** AI 310n, kilograms (catch-weight articles) */
  netWeightKg: z.number().nullable(),
  /** machine-readable `Gs1WarningCode`s; never thrown, never silent */
  warnings: z.array(z.string()),
});
export type BarcodeScanSummary = z.infer<typeof barcodeScanSummarySchema>;

export const resolvedArticleSchema = z.object({
  id: uuidSchema,
  articleNo: z.string(),
  name: z.string(),
  nameZh: z.string().nullable(),
  baseUnitId: uuidSchema,
  baseUnitCode: z.string(),
  supplierId: uuidSchema.nullable(),
  lotTracked: z.boolean(),
  expiryRequired: z.boolean(),
  catchWeight: z.boolean(),
  isActive: z.boolean(),
});
export type ResolvedArticle = z.infer<typeof resolvedArticleSchema>;

export const resolvedUnitSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  /** base units per one of this unit; `1` when it IS the base unit */
  qtyInBase: qtySchema,
});
export type ResolvedUnit = z.infer<typeof resolvedUnitSchema>;

/**
 * `resolveBarcode(raw, { supplierId? })`.
 *
 * Never throws for an unknown code — `matched: false` is the teach flow's cue
 * (M3 receiving). It DOES throw `E_BARCODE_AMBIGUOUS` when one code maps to
 * several articles and no supplier narrows it, because guessing there is how
 * stock ends up on the wrong item.
 */
export const barcodeResolutionSchema = z.object({
  raw: z.string(),
  matched: z.boolean(),
  source: barcodeMatchSourceSchema,
  scan: barcodeScanSummarySchema,
  barcodeId: uuidSchema.nullable(),
  article: resolvedArticleSchema.nullable(),
  /** the unit the code denotes (case/pallet GTIN), else the article's base unit */
  unit: resolvedUnitSchema.nullable(),
  /** BASE units added by one scan of this code */
  qtyPerScan: qtySchema,
  /** AI 10 */
  lot: z.string().nullable(),
  /** AI 15 (falls back to AI 17), ISO `YYYY-MM-DD` */
  bestBefore: z.string().nullable(),
  /** AI 00, when the label carried a logistic-unit SSCC */
  sscc: z.string().nullable(),
});
export type BarcodeResolution = z.infer<typeof barcodeResolutionSchema>;

/**
 * The typed port M3 (receiving) re-declares locally. Keep the shape stable —
 * `MasterDataModule` exports `BarcodeResolverService` implementing exactly this.
 */
export interface BarcodeResolverPort {
  resolveBarcode(
    raw: string,
    opts?: { supplierId?: string | null },
  ): Promise<BarcodeResolution>;
}

// ---------------------------------------------------------------------------
// history ("Historik" tab — reads audit_log)
// ---------------------------------------------------------------------------

export const auditEntrySchema = z.object({
  id: z.string(),
  at: z.iso.datetime(),
  action: z.string(),
  entity: z.string(),
  entityId: z.string().nullable(),
  userId: uuidSchema.nullable(),
  userName: z.string().nullable(),
  deviceId: uuidSchema.nullable(),
  commandId: uuidSchema.nullable(),
  changes: z
    .object({
      before: z.record(z.string(), z.unknown()),
      after: z.record(z.string(), z.unknown()),
    })
    .nullable(),
  context: z.record(z.string(), z.unknown()).nullable(),
});
export type AuditEntryDto = z.infer<typeof auditEntrySchema>;

export const auditHistoryResponseSchema = z.object({
  items: z.array(auditEntrySchema),
});
export type AuditHistoryResponse = z.infer<typeof auditHistoryResponseSchema>;

// ---------------------------------------------------------------------------
// command types (CommandBus registry keys — also the sync-command names)
// ---------------------------------------------------------------------------

/**
 * Registered on the CommandBus registry, so the SAME command type works from
 * the web and from `POST /api/sync/commands` (the Android outbox).
 * Master data's only genuinely scanner-facing write is teaching a barcode.
 */
export const MASTER_DATA_SYNC_COMMANDS = ['barcode.teach'] as const;

/** Every master-data command type, registered or inline. */
export const MASTER_DATA_COMMANDS = [
  'article.create',
  'article.update',
  'article.unit.upsert',
  'article.unit.remove',
  'articleCategory.create',
  'articleCategory.update',
  'articleCategory.remove',
  'unit.create',
  'unit.update',
  'unit.remove',
  'barcode.add',
  'barcode.update',
  'barcode.remove',
  'barcode.teach',
  'supplier.create',
  'supplier.update',
  'restaurant.create',
  'restaurant.update',
  'restaurantUser.link',
  'restaurantUser.unlink',
] as const;
export type MasterDataCommand = (typeof MASTER_DATA_COMMANDS)[number];
